import { useState, useRef, useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import axiosInstance from "../../lib/axios";

const langKeys = {
    eng: { titleKey: "title", tpKey: "touchpoints" },
    ar: { titleKey: "arabicTitle", tpKey: "arTouchpoints" },
    esp: { titleKey: "spanishTitle", tpKey: "espTouchpoints" },
    it: { titleKey: "italianTitle", tpKey: "itTouchpoints" },
    det: { titleKey: "detTitle", tpKey: "detTouchpoints" },
    fr: { titleKey: "frTitle", tpKey: "frTouchpoints" },
};

const translateCodes = { eng: 'en', ar: 'ar', esp: 'es', it: 'it', det: 'de', fr: 'fr' };

const languages = ['eng', 'ar', 'esp', 'it', 'det', 'fr'];

const convertAPIDataToSteps = (operation) => {
    if (!operation) {
        return { eng: [], ar: [], esp: [], it: [], det: [], fr: [] };
    }

    const steps = { eng: [], ar: [], esp: [], it: [], det: [], fr: [] };
    const engSteps = operation?.steps || [];
    const arSteps = operation?.arabicSteps || [];
    const espSteps = operation?.spanishSteps || [];
    const itSteps = operation?.italianSteps || [];
    const detSteps = operation?.germanSteps || [];
    const frSteps = operation?.frenchSteps || [];
    const engTouchpoints = operation?.touchpointOJ || [];
    const arTouchpoints = operation?.touchpointArOJ || [];
    const espTouchpoints = operation?.touchpointEsOJ || [];
    const itTouchpoints = operation?.touchpointItOJ || [];
    const detTouchpoints = operation?.touchpointDeOJ || [];
    const frTouchpoints = operation?.touchpointFrOJ || [];

    const maxSteps = Math.max(
        engSteps.length,
        arSteps.length,
        espSteps.length,
        itSteps.length,
        detSteps.length,
        frSteps.length
    );

    for (let i = 0; i < maxSteps; i++) {
        const engStepTitle = engSteps[i] || "";
        const arStepTitle = arSteps[i] || "";
        const espStepTitle = espSteps[i] || "";
        const itStepTitle = itSteps[i] || "";
        const detStepTitle = detSteps[i] || "";
        const frStepTitle = frSteps[i] || "";

        steps.eng.push({
            title: engStepTitle,
            touchpoints: engTouchpoints.filter(tp => tp.step === engStepTitle).map(tp => ({
                id: tp.id, pos: tp.pos, step: tp.step, title: tp.label,
            }))
        });

        steps.ar.push({
            arabicTitle: arStepTitle,
            arTouchpoints: arTouchpoints.filter(tp => tp.step === arStepTitle).map(tp => ({
                id: tp.id, pos: tp.pos, step: tp.step, arabicTitle: tp.label,
            }))
        });

        steps.esp.push({
            spanishTitle: espStepTitle,
            espTouchpoints: espTouchpoints.filter(tp => tp.step === espStepTitle).map(tp => ({
                id: tp.id, pos: tp.pos, step: tp.step, spanishTitle: tp.label,
            }))
        });

        steps.it.push({
            italianTitle: itStepTitle,
            itTouchpoints: itTouchpoints.filter(tp => tp.step === itStepTitle).map(tp => ({
                id: tp.id, pos: tp.pos, step: tp.step, italianTitle: tp.label,
            }))
        });

        steps.det.push({
            detTitle: detStepTitle,
            detTouchpoints: detTouchpoints.filter(tp => tp.step === detStepTitle).map(tp => ({
                id: tp.id, pos: tp.pos, step: tp.step, detTitle: tp.label,
            }))
        });

        steps.fr.push({
            frTitle: frStepTitle,
            frTouchpoints: frTouchpoints.filter(tp => tp.step === frStepTitle).map(tp => ({
                id: tp.id, pos: tp.pos, step: tp.step, frTitle: tp.label,
            }))
        });
    }

    return steps;
};

export const useOperationJourneyForm = (initialData = null) => {
    const formMethods = useForm({
        defaultValues: {
            industry: initialData?.industryDetails?.id || "",
            name: {
                eng: initialData?.name || "",
                ar: initialData?.arabicName || "",
                esp: initialData?.spanishName || "",
                it: initialData?.italianName || "",
                det: initialData?.germanName || "",
                fr: initialData?.frenchName || "",
            },
            description: {
                eng: initialData?.description || "",
                ar: initialData?.arabicDescription || "",
                esp: initialData?.spanishDescription || "",
                it: initialData?.italianDescription || "",
                det: initialData?.germanDescription || "",
                fr: initialData?.frenchDescription || "",
            },
        }
    });

    const [steps, setSteps] = useState(() => convertAPIDataToSteps(initialData));
    const [activeStepIndex, setActiveStepIndex] = useState(null);
    const [newTitle, setNewTitle] = useState({ eng: "", ar: "", esp: "", it: "", det: "", fr: "" });
    const [updatedTitle, setUpdatedTitle] = useState({ eng: "", ar: "", esp: "", it: "", det: "", fr: "" });
    const [title, setTitle] = useState({ eng: "", ar: "", esp: "", it: "", det: "", fr: "" });
    const [editingTouchpointId, setEditingTouchpointId] = useState(null);
    const [curvePos, setCurvePos] = useState(0.5);
    const [isTranslating, setIsTranslating] = useState(false);
    const [currentLangIndex, setCurrentLangIndex] = useState(0);

    const svgRef = useRef(null);
    const isDragging = useRef(false);

    useEffect(() => {
        if (initialData) {
            formMethods.reset({
                industry: initialData?.industryDetails?.id || "",
                name: {
                    eng: initialData?.name || "",
                    ar: initialData?.arabicName || "",
                    esp: initialData?.spanishName || "",
                    it: initialData?.italianName || "",
                    det: initialData?.germanName || "",
                    fr: initialData?.frenchName || "",
                },
                description: {
                    eng: initialData?.description || "",
                    ar: initialData?.arabicDescription || "",
                    esp: initialData?.spanishDescription || "",
                    it: initialData?.italianDescription || "",
                    det: initialData?.germanDescription || "",
                    fr: initialData?.frenchDescription || "",
                },
            });
            setSteps(convertAPIDataToSteps(initialData));
        }
    }, [initialData, formMethods]);

    const currentLang = languages[currentLangIndex];

    const areAllLangsFilled = (obj) => {
        if (!obj) return false;
        return languages.every((lang) => (obj?.[lang] || '').toString().trim().length > 0);
    };

    const bezier = (t, p0, p1, p2) =>
        (1 - t) * (1 - t) * p0 + 2 * (1 - t) * t * p1 + t * t * p2;

    const startDrag = (e) => {
        isDragging.current = true;
        window.addEventListener('mousemove', onDrag);
        window.addEventListener('mouseup', stopDrag);
    };

    const stopDrag = () => {
        isDragging.current = false;
        window.removeEventListener('mousemove', onDrag);
        window.removeEventListener('mouseup', stopDrag);
    };

    const onDrag = (e) => {
        if (!isDragging.current) return;
        const svg = svgRef.current;
        if (!svg) return;
        const rect = svg.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        let t = (mouseX - 10) / (190 - 10);
        t = Math.max(0, Math.min(1, t));
        setCurvePos(t.toFixed(2));
    };

    const handleStepTitleChange = (stepIndex, value) => {
        const { titleKey, tpKey } = langKeys[currentLang];
        setSteps((prev) => {
            const updated = {};
            Object.keys(prev).forEach((lang) => {
                updated[lang] = prev[lang].map((s, i) => {
                    if (i !== stepIndex || lang !== currentLang) return s;
                    const touchpoints = (s[tpKey] || []).map(tp => ({
                        ...tp,
                        step: value
                    }));
                    return { ...s, [titleKey]: value, [tpKey]: touchpoints };
                });
            });
            return updated;
        });
    };

    const handleTouchpointTitleChange = (stepIndex, tpIndex, value) => {
        const { tpKey, titleKey } = langKeys[currentLang];
        setSteps((prev) => {
            const updated = {};
            Object.keys(prev).forEach((lang) => {
                updated[lang] = prev[lang].map((step, i) => {
                    if (i !== stepIndex || lang !== currentLang) return step;
                    const tps = step[tpKey] || [];
                    const newTps = tps.map((tp, j) => (j === tpIndex ? { ...tp, [titleKey]: value } : tp));
                    return { ...step, [tpKey]: newTps };
                });
            });
            return updated;
        });
    };

    const handleAddStep = () => {
        if (!title[currentLang]) {
            toast.info("Please fill the title for the current language");
            return;
        }
        setSteps((prev) => {
            const updated = { ...prev };
            Object.keys(langKeys).forEach((lang) => {
                const { titleKey, tpKey } = langKeys[lang];
                const stepObj = {
                    [titleKey]: lang === currentLang ? (title[currentLang] || "") : "",
                    [tpKey]: [],
                };
                updated[lang] = [...(prev[lang] || []), stepObj];
            });
            return updated;
        });
        setTitle({ eng: "", ar: "", esp: "", it: "", det: "", fr: "" });
    };

    const handleRemoveStep = (stepIndex, onSuccess) => {
        setSteps((prev) => {
            const updated = {};
            Object.keys(prev).forEach((lang) => {
                updated[lang] = prev[lang].filter((_, i) => i !== stepIndex);
            });
            return updated;
        });
        if (onSuccess) onSuccess();
    };

    const handleAddTouchpoint = (stepIndex) => {
        if (!newTitle[currentLang]) {
            toast.info("Please fill the touchpoint title for the current language");
            return;
        }
        setSteps((prev) => {
            const updated = {};
            Object.keys(prev).forEach((lang) => {
                const { titleKey, tpKey } = langKeys[lang];
                const step = prev[lang][stepIndex];

                const newTP = {
                    pos: curvePos,
                    id: (step[tpKey]?.length || 0) + 1,
                    step: step[titleKey] || "",
                    [titleKey]: lang === currentLang ? (newTitle[currentLang] || "") : "",
                };
                updated[lang] = prev[lang].map((s, i) =>
                    i === stepIndex
                        ? { ...s, [tpKey]: [...(s[tpKey] || []), newTP] }
                        : s
                );
            });
            return updated;
        });
        setNewTitle({ eng: "", ar: "", esp: "", it: "", det: "", fr: "" });
        setActiveStepIndex(null);
        setCurvePos(0.5);
    };

    const handleSaveUpdatedTouchpoint = () => {
        if (!updatedTitle[currentLang]) {
            toast.info("Please fill the field for the current language");
            return;
        }
        const { stepIndex, tpIndex } = editingTouchpointId;
        setSteps((prev) => {
            const updated = {};
            Object.keys(prev).forEach((lang) => {
                const { tpKey, titleKey } = langKeys[lang];
                updated[lang] = prev[lang].map((step, i) => {
                    if (i !== stepIndex) return step;
                    const newTPs = (step[tpKey] || []).map((tp, j) =>
                        j === tpIndex
                            ? {
                                ...tp,
                                [titleKey]: lang === currentLang ? (updatedTitle[currentLang] || tp[titleKey]) : tp[titleKey],
                                pos: curvePos,
                            }
                            : tp
                    );
                    return { ...step, [tpKey]: newTPs };
                });
            });
            return updated;
        });
        setUpdatedTitle({ eng: "", ar: "", esp: "", it: "", det: "", fr: "" });
        setEditingTouchpointId(null);
        setCurvePos(0.5); // Reset to default
    };

    const handleDeleteTouchpoint = (stepIndex, tpIndex, onSuccess) => {
        setSteps((prev) => {
            const updated = {};
            Object.keys(prev).forEach((lang) => {
                const { tpKey } = langKeys[lang];
                updated[lang] = prev[lang].map((step, i) => {
                    if (i !== stepIndex) return step;
                    const currentTPs = step[tpKey] || [];
                    const filteredTPs = currentTPs.filter((_, j) => j !== tpIndex);
                    return { ...step, [tpKey]: filteredTPs };
                });
            });
            return updated;
        });
        if (onSuccess) onSuccess();
    };

    const translateJsonAllTargets = async (itemsMap) => {
        try {
            const response = await axiosInstance.post('/admin/translate', { items: itemsMap });
            return response.data?.translations || {};
        } catch (e) {
            return {};
        }
    };

    const translateAllFromEnglish = async () => {
        if (isTranslating) return;
        setIsTranslating(true);
        try {
            const engName = formMethods.getValues('name.eng') || '';
            const engDesc = formMethods.getValues('description.eng') || '';
            const engTitleKey = langKeys.eng.titleKey;
            const engTpKey = langKeys.eng.tpKey;
            const itemsMap = {};
            const applyPlans = [];
            const updatedSteps = {
                eng: (steps.eng || []).map(s => ({ ...s })),
                ar: (steps.ar || []).map(s => ({ ...s })),
                esp: (steps.esp || []).map(s => ({ ...s })),
                it: (steps.it || []).map(s => ({ ...s })),
                det: (steps.det || []).map(s => ({ ...s })),
                fr: (steps.fr || []).map(s => ({ ...s })),
            };

            for (const lang of languages) {
                if (lang === 'eng') continue;
                const targetTitleKey = langKeys[lang].titleKey;
                const targetTpKey = langKeys[lang].tpKey;

                const plan = { lang, targetTitleKey, targetTpKey, stepsPlan: [], tpPlan: [], translateName: false, translateDesc: false };
                if (engName) { itemsMap['name'] = engName; plan.translateName = true; }
                if (engDesc) { itemsMap['desc'] = engDesc; plan.translateDesc = true; }

                updatedSteps[lang] = (updatedSteps[lang] || []).slice();
                if ((updatedSteps[lang] || []).length < (steps.eng || []).length) {
                    updatedSteps[lang] = (steps.eng || []).map(() => ({ [targetTitleKey]: '', [targetTpKey]: [] }));
                }
                for (let i = 0; i < (steps.eng || []).length; i++) {
                    const engStep = steps.eng[i] || {};
                    const engStepTitle = engStep[engTitleKey] || '';
                    if (engStepTitle) { const k = `step_${i}`; itemsMap[k] = engStepTitle; plan.stepsPlan.push({ i, key: k }); }
                    const engTps = engStep[engTpKey] || [];
                    for (let j = 0; j < engTps.length; j++) {
                        const engTp = engTps[j] || {};
                        const engLabel = engTp[engTitleKey] || engTp.title || '';
                        if (engLabel) { const k = `tp_${i}_${j}`; itemsMap[k] = engLabel; plan.tpPlan.push({ i, j, key: k, engTp }); }
                    }
                }
                applyPlans.push(plan);
            }

            const translationsByLang = await translateJsonAllTargets(itemsMap);
            for (const plan of applyPlans) {
                const { lang, targetTitleKey, targetTpKey } = plan;
                const code = translateCodes[lang];
                if (plan.translateName && engName) {
                    const text = translationsByLang?.[code]?.['name'] || engName;
                    formMethods.setValue(`name.${lang}`, text, { shouldDirty: true });
                }
                if (plan.translateDesc && engDesc) {
                    const text = translationsByLang?.[code]?.['desc'] || engDesc;
                    formMethods.setValue(`description.${lang}`, text, { shouldDirty: true });
                }
                updatedSteps[lang] = (updatedSteps[lang] || []).slice();
                if ((updatedSteps[lang] || []).length < (steps.eng || []).length) {
                    updatedSteps[lang] = (steps.eng || []).map(() => ({ [targetTitleKey]: '', [targetTpKey]: [] }));
                }
                for (let i = 0; i < (steps.eng || []).length; i++) {
                    const engStep = steps.eng[i] || {};
                    const tgtStep = updatedSteps[lang][i] || {};
                    const stepPlan = plan.stepsPlan.find(p => p.i === i);
                    if (stepPlan) {
                        const text = translationsByLang?.[code]?.[stepPlan.key] || itemsMap[stepPlan.key] || '';
                        tgtStep[targetTitleKey] = text;
                    }
                    const engTps = engStep[engTpKey] || [];
                    const tgtTps = (tgtStep[targetTpKey] || []).slice();
                    if (tgtTps.length < engTps.length) {
                        while (tgtTps.length < engTps.length) {
                            const seed = engTps[tgtTps.length] || {};
                            tgtTps.push({ ...seed });
                        }
                    }
                    for (let j = 0; j < engTps.length; j++) {
                        const tpPlan = plan.tpPlan.find(p => p.i === i && p.j === j);
                        if (tpPlan) {
                            const text = translationsByLang?.[code]?.[tpPlan.key] || itemsMap[tpPlan.key] || '';
                            const currentTarget = tgtTps[j] || {};
                            tgtTps[j] = { ...currentTarget, ...tpPlan.engTp, [targetTitleKey]: text };
                        }
                    }
                    updatedSteps[lang][i] = { ...tgtStep, [targetTpKey]: tgtTps };
                }
            }
            setSteps(updatedSteps);
            toast.success('Translated empty fields from English');
        } catch (e) {
            toast.error('Failed to translate. Please try again.');
        } finally {
            setIsTranslating(false);
        }
    };

    const prepareSubmitData = (formData) => {
        const stepsArray = {};
        Object.keys(steps).forEach((lang) => {
            stepsArray[lang] = steps[lang].map((s) => {
                const key = lang === "ar" ? "arabicTitle" :
                    lang === "fr" ? "frTitle" :
                        lang === "det" ? "detTitle" :
                            lang === "esp" ? "spanishTitle" :
                                lang === "it" ? "italianTitle" :
                                    "title";
                return s[key];
            });
        });

        const touchpointsOJ = {
            eng: steps.eng.flatMap(step =>
                step.touchpoints.map(tp => ({
                    id: tp.id,
                    pos: tp.pos,
                    step: tp.step,
                    label: tp.title,
                }))
            ),
            ar: steps.ar.flatMap(step =>
                step.arTouchpoints.map(tp => ({
                    id: tp.id,
                    pos: tp.pos,
                    step: step.arabicTitle,
                    label: tp.arabicTitle,
                }))
            ),
            fr: steps.fr.flatMap(step =>
                step.frTouchpoints.map(tp => ({
                    id: tp.id,
                    pos: tp.pos,
                    step: step.frTitle,
                    label: tp.frTitle,
                }))
            ),
            det: steps.det.flatMap(step =>
                step.detTouchpoints.map(tp => ({
                    id: tp.id,
                    pos: tp.pos,
                    step: step.detTitle,
                    label: tp.detTitle,
                }))
            ),
            esp: steps.esp.flatMap(step =>
                step.espTouchpoints.map(tp => ({
                    id: tp.id,
                    pos: tp.pos,
                    step: step.spanishTitle,
                    label: tp.spanishTitle,
                }))
            ),
            it: steps.it.flatMap(step =>
                step.itTouchpoints.map(tp => ({
                    id: tp.id,
                    pos: tp.pos,
                    step: step.italianTitle,
                    label: tp.italianTitle,
                }))
            ),
        };

        return {
            industry: formData.industry,
            name: formData.name.eng,
            arabicName: formData.name.ar,
            frenchName: formData.name.fr,
            germanName: formData.name.det,
            spanishName: formData.name.esp,
            italianName: formData.name.it,
            description: formData.description.eng,
            arabicDescription: formData.description.ar,
            frenchDescription: formData.description.fr,
            germanDescription: formData.description.det,
            spanishDescription: formData.description.esp,
            italianDescription: formData.description.it,
            steps: stepsArray.eng,
            arabicSteps: stepsArray.ar,
            frenchSteps: stepsArray.fr,
            germanSteps: stepsArray.det,
            spanishSteps: stepsArray.esp,
            italianSteps: stepsArray.it,
            touchpointOJ: touchpointsOJ.eng,
            touchpointArOJ: touchpointsOJ.ar,
            touchpointFrOJ: touchpointsOJ.fr,
            touchpointDeOJ: touchpointsOJ.det,
            touchpointEsOJ: touchpointsOJ.esp,
            touchpointItOJ: touchpointsOJ.it,
        };
    };

    return {
        formMethods,
        steps,
        currentLang,
        currentLangIndex,
        setCurrentLangIndex,
        activeStepIndex,
        setActiveStepIndex,
        newTitle,
        setNewTitle,
        updatedTitle,
        setUpdatedTitle,
        title,
        setTitle,
        editingTouchpointId,
        setEditingTouchpointId,
        curvePos,
        setCurvePos,
        svgRef,
        isTranslating,
        areAllLangsFilled,
        bezier,
        startDrag,
        handleStepTitleChange,
        handleTouchpointTitleChange,
        handleAddStep,
        handleRemoveStep,
        handleAddTouchpoint,
        handleSaveUpdatedTouchpoint,
        handleDeleteTouchpoint,
        translateAllFromEnglish,
        prepareSubmitData,
        langKeys,
        languages,
    };
};